import {
  Flex,
  Button,
  VStack,
  Box,
  Text,
  Tooltip as ChakraTooltip,
} from "@chakra-ui/react";
import ClaimKepConfirmation from "@components/ClaimKepConfirmation";
import {
  CLAIM_KEP_CTA,
  CONNECT_WALLET_CTA,
  formatEtherNumber,
  toFixedWithoutRounding,
} from "@constants/common";

import Tooltip from "@components/Tooltip";
import {
  useAccount,
  usePublicClient,
  useReadContract,
  useSwitchChain,
  useWalletClient,
} from "wagmi";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useElMerkleProof } from "@hooks/useElMerkleProof";
import TransactionModal from "@components/TransactionModal";
import BaseImage from "@components/BaseImage";
import { emitReduxEvent, isErrorRejectedByUser, trackEvent } from "utils";
import {
  CLAIM_KEP,
  CLAIM_KEP_CLICK,
  CONNECT_WALLET_CLICK,
} from "@constants/analytics";
import { EVENTS } from "@constants/reduxEvents";
import { useState } from "react";
import { formatEther, parseEther } from "ethers/lib/utils.js";
import { useElMerkleRoot } from "@hooks/useElMerkleRoot";
import { getKelpStats } from "@services/index";
import { useElMerkleProofIndex } from "@hooks/useElMerkleProofIndex";
import useDappConfig from "@hooks/useDappConfig";
import useDappChain from "@hooks/useDappChain";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import { BigNumber } from "ethers";
import { contractConfig } from "@config/contractsConfig";
import { mainnet } from "viem/chains";

const getTransactionInitialState = () => ({
  open: false,
  hash: "",
  error: "",
  hasUserDenied: false,
});

function useSendClaimTransaction() {
  const { data: signer } = useWalletClient();
  const index = useElMerkleProofIndex();

  const { config } = useDappConfig();
  const { address } = useAccount();
  const provider = usePublicClient();

  return useMutation({
    mutationFn: async (args: {
      cumulativeAmount: string;
      merkleProof: `0x${string}`[];
    }) => {
      if (!signer || !provider || !address || !index) return undefined;

      if (!config.interactionPools.merkleDistributor) {
        return;
      }

      let gasLimit = await provider.estimateContractGas({
        ...config.interactionPools.merkleDistributor,
        functionName: "claim",
        args: [index, address, args.cumulativeAmount, args.merkleProof],
        address,
      });

      if (gasLimit < 115_000n) {
        gasLimit = 115_000n;
      }

      const hash = await signer.writeContract({
        ...config.interactionPools.merkleDistributor,
        functionName: "claim",
        chain: mainnet,
        args: [index, address, args.cumulativeAmount, args.merkleProof],
        gas: gasLimit * 2n,
      });

      return {
        hash,
        wait: () =>
          provider.waitForTransactionReceipt({
            hash,
          }),
      };
    },
  });
}

const ClaimKep = () => {
  const { isConnected, address } = useAccount();
  // const address = "0xc6160F5bC3C673AC390f11c492E8ED0d0693579A";
  const [isTxModalOpen, setIsTxModalOpen] = useState(
    getTransactionInitialState()
  );
  const [isConfirmModalOpen, setIsConfirmTxModalOpen] = useState({
    open: false,
    text: "",
  });
  const { mutate: sendClaimTransactionFn } = useSendClaimTransaction();
  const { isClaimSupported, config } = useDappConfig();
  const mainnetConfig = contractConfig[mainnet.id];
  const { chain } = useDappChain();
  const { switchChain } = useSwitchChain();

  const { data: kelpStats } = useQuery({
    queryKey: ["kelp-stats", address],
    queryFn: () => {
      return getKelpStats(address!);
    },
    enabled: address !== undefined,
  });

  const { data: userClaimsData, refetch: refetchUserClaims } = useReadContract({
    ...mainnetConfig.interactionPools.merkleDistributor,
    functionName: "userClaims",
    chainId: mainnet.id,
    args: [address],
    query: {
      enabled: !!address,
    },
  });

  // console.log("userClaimsData", userClaimsData);

  const { data: elMerkleProof, refetch: elMerkleProofRefetch } =
    useElMerkleProof(address);

  const totalSupplyData = useReadContract({
    ...mainnetConfig.interactionPools.kep,
    functionName: "totalSupply",
    chainId: mainnet.id,
  });

  const balanceInMerkle = useReadContract({
    ...mainnetConfig.interactionPools.kep,
    functionName: "balanceOf",
    chainId: mainnet.id,
    args: [mainnetConfig.interactionPools.merkleDistributor.address],
  });

  const { data: elMerkleRoot, refetch: elMerkleRootRefetch } =
    useElMerkleRoot();

  const { openConnectModal } = useConnectModal();

  const refetchAll = () => {
    refetchUserClaims();
    elMerkleProofRefetch();
    balanceInMerkle.refetch();
    elMerkleRootRefetch();
  };

  const getButtonText = () => {
    if (!config.interactionPools.kep) {
      return "Switch to Ethereum";
    }
    if (!isConnected) {
      return CONNECT_WALLET_CTA;
    } else {
      if (!isClaimSupported) {
        return "Switch network";
      }
      return CLAIM_KEP_CTA;
    }
  };

  const onClick = () => {
    if (!isConnected) {
      openConnectModal?.();
      trackEvent(CONNECT_WALLET_CLICK, {
        cta_location: "claim_page",
        cta: CONNECT_WALLET_CTA,
      });
    } else if (!isClaimSupported) {
      emitReduxEvent(EVENTS.SWITCH_NETWORK);
    } else if (elMerkleProof?.data) {
      trackEvent(CLAIM_KEP_CLICK, elMerkleProof?.data);
      setIsConfirmTxModalOpen({
        open: true,
        text: "Please sign the transaction",
      });
      sendClaimTransactionFn(
        {
          cumulativeAmount: elMerkleProof?.data?.el,
          merkleProof: elMerkleProof?.data?.proof,
        },
        {
          async onSuccess(res) {
            setIsConfirmTxModalOpen({
              open: true,
              text: "Transaction in progress",
            });

            try {
              const receipt = await res!.wait();
              trackEvent(CLAIM_KEP, receipt);

              setIsTxModalOpen({
                open: true,
                hash: receipt.transactionHash,
                error: "",
                hasUserDenied: false,
              });
              refetchAll();
            } catch (error) {
              trackEvent(CLAIM_KEP, { error: String(error) });
              setIsTxModalOpen({
                open: true,
                // @ts-ignore
                hash: error.transactionHash,
                // @ts-ignore
                error: error?.reason ?? error?.error ?? error?.message ?? "",
                hasUserDenied: isErrorRejectedByUser(error),
              });
            } finally {
              setIsConfirmTxModalOpen({
                open: false,
                text: "",
              });
            }
          },
          async onError(error) {
            trackEvent(CLAIM_KEP, error);
            setIsTxModalOpen({
              open: true,
              // @ts-ignore
              hash: error.transactionHash,
              // @ts-ignore
              error: error?.reason ?? error?.error ?? error?.message ?? "",
              hasUserDenied: isErrorRejectedByUser(error),
            });
            setIsConfirmTxModalOpen({
              open: false,
              text: "",
            });
          },
        }
      );
    }
  };

  // @ts-ignore
  const userClaimedAmount = BigNumber.from(userClaimsData?.[1] || "0");

  const totalClaimable =
    elMerkleProof?.data === undefined
      ? undefined
      : elMerkleProof?.data === null
      ? BigNumber.from(0)
      : BigNumber.from(elMerkleProof?.data?.el);

  const claimableElPoints =
    userClaimedAmount && totalClaimable
      ? totalClaimable.sub(userClaimedAmount)
      : undefined;

  const totalElPoints =
    kelpStats?.data?.value?.elPoints &&
    parseEther(kelpStats?.data?.value?.elPoints);

  return (
    <>
      <Flex justifyContent="center" p={{ base: "0 16px", sm: "0 40px", md: 0 }}>
        <Flex
          flexDir="column"
          gap="16px"
          mt={{ base: "16px", xl: "0px" }}
          w={{ base: "100%", md: "648px" }}
        >
          <Flex gap="16px" justifyContent="stretch">
            <Box
              borderRadius="1rem"
              bg="backgroundField"
              border="1px solid"
              color="textEigen"
              borderColor={"backgroundDivider"}
              p="16px"
              flex={"1"}
              position={"relative"}
              overflow={"hidden"}
            >
              <Text
                fontSize={{ base: "16px", md: "24px" }}
                lineHeight={{ base: "24px", md: "38px" }}
                fontWeight={{ base: "600", md: "800" }}
              >
                {elMerkleRoot?.data
                  ? Number(
                      toFixedWithoutRounding(
                        formatEther(
                          // eslint-disable-next-line no-undef
                          BigInt(
                            Number(elMerkleRoot?.data.totalElPoints)
                          ).toString()
                        ).toString(),
                        2
                      )
                    ).toLocaleString()
                  : "-"}
              </Text>
              <Text
                fontSize={{ base: "10px", md: "12px" }}
                lineHeight={{ base: "15px", md: "18px" }}
                fontWeight="400"
                opacity={"80%"}
              >
                EL points earned on Kelp
              </Text>
              <Box
                height={"84px"}
                width={"84px"}
                position={"absolute"}
                bottom={"-12px"}
                right={"-12px"}
                hideBelow="md"
                pos="relative"
              >
                <BaseImage src="/assets/claim/eigen.svg" alt="eigen" fill />
              </Box>
            </Box>
            <Box
              borderRadius="1rem"
              bg="backgroundField"
              border="1px solid"
              color="textPrimary"
              borderColor={"backgroundDivider"}
              p="16px"
              flex={"1"}
              position={"relative"}
              overflow={"hidden"}
            >
              <Text
                fontSize={{ base: "16px", md: "24px" }}
                lineHeight={{ base: "24px", md: "38px" }}
                fontWeight={{ base: "600", md: "800" }}
              >
                {totalSupplyData.data && balanceInMerkle.data
                  ? Number(
                      toFixedWithoutRounding(
                        formatEther(
                          String(
                            (totalSupplyData.data as bigint) -
                              (balanceInMerkle.data as bigint)
                          )
                        ),
                        2
                      )
                    ).toLocaleString()
                  : "-"}
              </Text>
              <Text
                fontSize={{ base: "10px", md: "12px" }}
                lineHeight={{ base: "15px", md: "18px" }}
                fontWeight="400"
                opacity={"80%"}
              >
                $KEP tokens claimed
              </Text>
              <Box
                height={"84px"}
                width={"84px"}
                position={"absolute"}
                bottom={"-12px"}
                right={"-12px"}
                hideBelow="md"
                pos="relative"
              >
                <BaseImage src="/assets/claim/kep.svg" alt="kelp" fill />
              </Box>
            </Box>
          </Flex>
          <Flex
            borderRadius="24px"
            bg="backgroundCard"
            p={{ base: "16px 16px 24px", md: "40px" }}
            flexDir="column"
            gap={{ base: "32px", md: "40px" }}
            w={"100%"}
          >
            <Flex justifyContent={"space-between"} alignItems={"flex-start"}>
              <Text
                fontSize={{ base: "18px", md: "32px" }}
                lineHeight={{ base: "20px", md: "38px" }}
                fontWeight="600"
                color="textPrimary"
              >
                Claim $KEP
              </Text>
              <Text
                p={"4px 6px"}
                bg={"backgroundField"}
                fontWeight={"400"}
                fontSize={"10px"}
                lineHeight={"15px"}
                rounded={"6px"}
              >
                1 $KEP = 1 EL Point
              </Text>
            </Flex>

            <Flex flexDir="column" gap={{ base: "8px", md: "16px" }}>
              {/* <Flex
                justifyContent={"space-between"}
                fontSize={{ base: "14px", md: "16px" }}
                lineHeight={"24px"}
                fontWeight={"400"}
              >
                <Flex alignItems={"center"} gap="4px">
                  <Text>EL Points Balance</Text>
                </Flex>
                <Text>
                  {userClaimsData?.data?.cumulativeAmount &&
                  kelpStats?.data.value.elPoints
                    ? formatEtherNumber(
                        parseEther(kelpStats?.data.value.elPoints)
                          // @ts-ignore
                          .sub(userClaimsData?.data?.cumulativeAmount)
                          .toString()
                      )
                    : "-"}{" "}
                  EL Points
                </Text>
              </Flex> */}
              <Flex
                justifyContent={"space-between"}
                fontSize={{ base: "14px", md: "16px" }}
                lineHeight={"24px"}
                fontWeight={"400"}
              >
                <Flex alignItems={"center"} gap="4px">
                  <Text>Claimable EL Points</Text>
                  <Tooltip
                    label={
                      <Box>
                        EL points accumulated till last Tuesday <br /> are made
                        available for conversion
                      </Box>
                    }
                  />
                </Flex>
                <Text>
                  <Text fontWeight={"700"} as="span">
                    {claimableElPoints
                      ? formatEtherNumber(claimableElPoints.toString())
                      : "-"}
                  </Text>{" "}
                  EL Points
                </Text>
              </Flex>
              <Flex
                justifyContent={"space-between"}
                fontSize={{ base: "10px", md: "12px" }}
                lineHeight={"18px"}
                fontWeight={"400"}
              >
                <Flex alignItems={"center"} gap="4px">
                  <Text>Fee</Text>
                  <Tooltip
                    label={<Box>Fee (0.5%) for DeFi enablement of KEP</Box>}
                  />
                </Flex>
                <Text>
                  {claimableElPoints === undefined
                    ? "-"
                    : formatEtherNumber(
                        claimableElPoints.mul(5).div(1000).toString()
                      )}{" "}
                  KEP
                </Text>
              </Flex>
            </Flex>

            <ChakraTooltip
              label="KEP claims are currently paused"
              color="textSecondary"
              fontSize="14px"
              bg="backgroundField"
              p="16px 32px"
              borderRadius="8px"
              placement="top"
              w="100%"
              pointerEvents="all"
            >
              <Button
                borderRadius="12px"
                bg="textPrimary"
                p="16px 64px"
                h="56px"
                mt="12px"
                width="100%"
                _hover={{
                  bg: "textPrimary",
                }}
                onClick={() => {
                  isClaimSupported
                    ? onClick
                    : () => switchChain({ chainId: mainnet.id });
                }}
                isDisabled={true}
              >
                <Text
                  fontSize={"16px"}
                  lineHeight={"24px"}
                  fontWeight={"600"}
                  color="backgroundCard"
                >
                  {getButtonText()}
                </Text>
              </Button>
            </ChakraTooltip>

            <Text
              color="textSecondary"
              fontSize="14px"
              display={{ base: "block", md: "none" }}
              mt="-24px"
            >
              KEP claims are currently paused
            </Text>
          </Flex>
          <Flex
            direction={"column"}
            borderRadius="24px"
            bg="backgroundCard"
            p={{ base: "24px 16px", md: "40px" }}
            w={{ base: "100%", md: "648px" }}
            gap={{ base: "24px" }}
          >
            <Flex justifyContent={"space-between"} alignItems={"center"}>
              <VStack spacing={"8px"} alignItems={"flex-start"}>
                <Text
                  fontSize={{ base: "12px", md: "14px" }}
                  lineHeight={{ base: "18px", md: "21px" }}
                  fontWeight={{ base: "400", md: "600" }}
                >
                  My Claimed KEP
                </Text>
                <Text
                  fontSize={{ base: "24px", md: "32px" }}
                  lineHeight={{ base: "28px", md: "48px" }}
                  fontWeight={{ base: "700", md: "700" }}
                >
                  {userClaimedAmount
                    ? formatEtherNumber(
                        userClaimedAmount.mul(995).div(1000).toString()
                      )
                    : "-"}{" "}
                  KEP
                </Text>
              </VStack>
              <Box lineHeight={0} hideBelow={"md"} pos="relative">
                <BaseImage src={"/assets/claim/kep.svg"} alt="kelp" fill />
              </Box>
            </Flex>
            <Flex direction={"column"} gap="16px">
              <Text
                fontSize={{ base: "12px", md: "14px" }}
                lineHeight={{ base: "18px", md: "21px" }}
                fontWeight={{ base: "400", md: "600" }}
              >
                My EL Points
              </Text>
              <Flex
                justifyContent={"space-between"}
                fontSize={{ base: "14px", md: "16px" }}
                lineHeight={"24px"}
                fontWeight={"400"}
              >
                <Text>Converted</Text>
                <Text>
                  <Text fontWeight={"700"} as="span">
                    {userClaimedAmount
                      ? formatEtherNumber(userClaimedAmount.toString())
                      : "-"}
                  </Text>{" "}
                  EL Points
                </Text>
              </Flex>
              <Flex
                justifyContent={"space-between"}
                fontSize={{ base: "14px", md: "16px" }}
                lineHeight={"24px"}
                fontWeight={"400"}
              >
                <Text>Available for future cycles</Text>
                <Text>
                  <Text fontWeight={"700"} as="span">
                    {totalElPoints && userClaimedAmount
                      ? formatEtherNumber(
                          totalElPoints.sub(userClaimedAmount).toString()
                        )
                      : "-"}
                  </Text>{" "}
                  EL Points
                </Text>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
      {claimableElPoints && (
        <ClaimKepConfirmation
          isOpen={isConfirmModalOpen.open}
          onClose={() => {
            setIsConfirmTxModalOpen({
              open: false,
              text: "",
            });
          }}
          elPoints={claimableElPoints}
          actionText={isConfirmModalOpen.text}
        />
      )}
      <TransactionModal
        isOpen={isTxModalOpen.open}
        hash={isTxModalOpen.hash}
        error={isTxModalOpen.error}
        transactionType={""}
        isTxnProcessing={false}
        hasUserDenied={isTxModalOpen.hasUserDenied}
        closeAlert={() => setIsTxModalOpen(getTransactionInitialState())}
        handleTxView={() => {
          const url = `${chain.blockExplorers?.default.url}/tx/${isTxModalOpen.hash}`;
          window.open(url, "_blank");
        }}
        chainId={chain.id}
      />
    </>
  );
};

export default ClaimKep;
